// Participation outcomes (Core v2.6, offer v2.10). Only a fresh, consistent Core read (GET /member/activities) confirms anything;
// a successful POST alone is never shown as commitment or cancellation.
import { participationState, own } from "./memberDerive";

export const ACTION_FAIL = {
  join: "Die Anfrage wurde nicht übermittelt",
  withdraw: "Der Rückzug wurde nicht übermittelt",
  leave: "Die Absage wurde nicht übermittelt",
  unwait: "Das Verlassen der Warteliste wurde nicht übermittelt",
  unoffer: "Das Zurückziehen des Angebots wurde nicht übermittelt",
};

const UNKNOWN = {
  join: "Übermittelt – dein Teilnahmestatus konnte aber nicht bestätigt werden. Prüfe den Status erneut, bevor du etwas anderes tust.",
  withdraw: "Übermittelt – ob deine Anfrage zurückgezogen ist, konnte nicht bestätigt werden. Prüfe den Status erneut.",
  leave: "Übermittelt – ob deine Absage angekommen ist, konnte nicht bestätigt werden. Prüfe den Status erneut.",
  unwait: "Übermittelt – ob du die Warteliste verlassen hast, konnte nicht bestätigt werden. Prüfe den Status erneut.",
  unoffer: "Übermittelt – ob das Platzangebot zurückgezogen ist, konnte nicht bestätigt werden. Prüfe den Status erneut.",
};

const DONE = { withdraw: "Deine Anfrage ist zurückgezogen.", leave: "Deine Teilnahme ist abgesagt.", unwait: "Du stehst nicht mehr auf der Warteliste.", unoffer: "Du hast das Platzangebot zurückgezogen." };
const STILL = {
  withdraw: "Rückzug übermittelt – das VISIBILIS-System zeigt die Anfrage noch als offen.",
  leave: "Absage übermittelt – das VISIBILIS-System führt dich noch als Teilnehmerin oder Teilnehmer.",
  unwait: "Übermittelt – das VISIBILIS-System führt dich noch auf der Warteliste.",
  unoffer: "Übermittelt – das VISIBILIS-System zeigt das Platzangebot noch als offen.",
};

// All cancelling kinds use POST …/leave (v2.6); joining again after "storniert" reuses the same entry.
export const actionPath = (kind) => (kind === "join" ? "join" : "leave");

// POST actions the displayed state allows; none while the status is unknown. A full activity is no reason to block:
// v2.6 puts the request on the waitlist instead of answering 409. An offer can be accepted or withdrawn (…/leave).
export function participationActions(state, { blocked = false } = {}) {
  if (blocked) return { join: false, withdraw: false, leave: false, unwait: false, accept: false, unoffer: false };
  return { join: state === "none" || state === "cancelled", withdraw: state === "requested", leave: state === "registered" || state === "confirmed", unwait: state === "waitlisted",
    accept: state === "offered", unoffer: state === "offered" };
}

const result = (kind, kindOf, state, message) => ({ kind, result: kindOf, state, message });
const OFFER_PENDING = "Dir ist ein Platz angeboten. Für dich ist noch kein Platz reserviert – bestätige das Angebot, solange ein Platz frei ist.";

export function participationOutcome(kind, { postError = null, ok = false, activity = null } = {}) {
  if (postError) return result(kind, "failed", null, postError);
  if (!ok || !activity) return result(kind, "unknown", "unknown", UNKNOWN[kind]);
  const s = participationState(activity);
  if (s === "activity_cancelled") return result(kind, "info", s, "Diese Aktivität wurde abgesagt.");
  if (s === "unknown") return result(kind, "unknown", s, UNKNOWN[kind]);
  if (kind === "join") {
    if (s === "confirmed") return result(kind, "success", s, "Deine Teilnahme ist bestätigt.");
    if (s === "registered") return result(kind, "success", s, "Du bist eingetragen.");
    if (s === "requested") return result(kind, "pending", s, "Anfrage übermittelt. Zugesagt ist erst, wenn die Gastgebenden bestätigen.");
    if (s === "waitlisted") return result(kind, "pending", s, "Die Aktivität ist voll – du stehst auf der Warteliste. Zugesagt ist damit nichts; frei werdende Plätze werden nicht automatisch vergeben.");
    if (s === "offered") return result(kind, "pending", s, OFFER_PENDING);
    if (s === "declined") return result(kind, "info", s, "Deine Anfrage wurde abgelehnt.");
    return result(kind, "info", s, "Das VISIBILIS-System zeigt noch keine Teilnahme – es ist nichts bestätigt.");
  }
  if ((s === "none" || s === "cancelled") && own(activity, "joined") === false) return result(kind, "success", s, DONE[kind]);
  return result(kind, "info", s, STILL[kind]);
}

// v2.10 POST /member/activities/{id}/offer/accept (Bearer, no body): 200 · 409 capacity_conflict | offer_not_open | activity_closed · 404 · 401.
export const OFFER_ACCEPT_PATH = (id) => `/member/activities/${encodeURIComponent(id)}/offer/accept`;
export const OFFER_CONFLICTS = ["capacity_conflict", "offer_not_open", "activity_closed"];

const codeOf = (v) => (typeof v === "string" ? v.trim() : "");
function conflictCode(data) {
  const d = own(data, "detail");
  const code = [codeOf(d), codeOf(own(d, "code")), codeOf(own(data, "code")), codeOf(own(data, "error"))].find(Boolean) || "";
  return OFFER_CONFLICTS.includes(code) ? code : "";
}

// Request result → "ok" | one of OFFER_CONFLICTS | "conflict" | "not_found" | "session" | "blocked" | "unclear".
// Network loss, timeouts, 5xx and every undocumented answer are "unclear": the POST may or may not have arrived.
export function offerPostKind(r) {
  if (own(r, "guard") === true) return "blocked";
  const s = own(r, "status");
  if (Number.isInteger(s) && s >= 200 && s < 300) return "ok";
  if (s === 409) return conflictCode(own(r, "data")) || "conflict";
  if (s === 404) return "not_found";
  if (s === 401) return "session";
  return "unclear";
}

// Every answer except a blocked request or an ended session is followed by a fresh GET; only that read decides.
export const offerNeedsRead = (post) => post !== "blocked" && post !== "session";

const NOT_ACCEPTED = {
  offer_not_open: "Das Platzangebot ist nicht mehr offen. Angenommen ist nichts.",
  activity_closed: "Für diese Aktivität werden keine Teilnahmen mehr angenommen. Angenommen ist nichts.",
  capacity_conflict: "Der angebotene Platz ist inzwischen vergeben. Angenommen ist nichts.",
  not_found: "Diese Aktivität ist nicht (mehr) verfügbar oder für dich nicht sichtbar. Angenommen ist nichts.",
  conflict: "Das VISIBILIS-System hat die Annahme abgelehnt. Angenommen ist nichts.",
};
const ACCEPT_UNKNOWN = "Ob das Platzangebot angenommen wurde, konnte nicht bestätigt werden. Prüfe den Status erneut, bevor du etwas anderes tust.";

// post = offerPostKind(…) or "recheck"; read = { ok, activity } from GET /member/activities (null when not read).
export function offerAcceptOutcome(post, read) {
  const r = (state, kindOf, message) => result("accept", kindOf, state, message);
  if (post === "blocked") return r(null, "failed", "Die Annahme wurde nicht übermittelt. In dieser Umgebung ist das Senden gesperrt.");
  if (post === "session") return r(null, "failed", "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an. Angenommen ist nichts.");
  if (!read || own(read, "ok") !== true) return r("unknown", "unknown", ACCEPT_UNKNOWN);
  const a = own(read, "activity");
  if (!a) return r(null, "failed", NOT_ACCEPTED.not_found);
  const s = participationState(a);
  if (s === "unknown") return r(s, "unknown", ACCEPT_UNKNOWN);
  if (s === "confirmed") return r(s, "success", "Angebot angenommen – deine Teilnahme ist bestätigt.");
  if (s === "activity_cancelled") return r(s, "info", "Diese Aktivität wurde abgesagt. Angenommen ist nichts.");
  if (s === "waitlisted") return r(s, "pending", post === "capacity_conflict"
    ? "Der angebotene Platz ist inzwischen vergeben. Du stehst wieder auf der Warteliste – zugesagt ist nichts."
    : "Du stehst auf der Warteliste – zugesagt ist nichts.");
  if (s === "offered") {
    if (post === "unclear") return r(s, "info", "Die Verbindung war unterbrochen. Das VISIBILIS-System zeigt das Platzangebot weiterhin als offen – angenommen ist nichts. Du kannst es erneut versuchen.");
    if (post === "ok" || post === "recheck") return r(s, "info", "Das VISIBILIS-System zeigt das Platzangebot noch als offen. Angenommen ist erst, was als bestätigt angezeigt wird.");
    return r(s, "info", NOT_ACCEPTED[post] || NOT_ACCEPTED.conflict);
  }
  return r(s, "info", NOT_ACCEPTED[post] || "Das VISIBILIS-System zeigt keine bestätigte Teilnahme – angenommen ist nichts.");
}
